import { useCallback, useEffect, useRef, useState } from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { hasNewVersion } from '../utils/versionCheck'

const CHECK_INTERVAL_MS = 5 * 60 * 1000 // poll every 5 minutes
const INITIAL_DELAY_MS = 15 * 1000 // first check shortly after load, not on the critical path

// Non-blocking prompt shown when a newer build has been deployed, inviting the user to
// refresh and pull the latest chunks before they hit a stale-chunk error. Pairs with the
// reactive guarded auto-reload (utils/chunkReload) — this is the proactive, gentle path.
const NewVersionNudge = () => {
  const [available, setAvailable] = useState(false)
  const foundRef = useRef(false)

  useEffect(() => {
    // The hashed-asset comparison is only meaningful for a built (prod) bundle.
    if (!import.meta.env.PROD) return

    const check = async () => {
      if (foundRef.current || document.visibilityState === 'hidden') return
      try {
        if (await hasNewVersion()) {
          foundRef.current = true
          setAvailable(true)
        }
      } catch (_) {
        // offline / transient fetch failure — ignore and retry on the next tick
      }
    }

    const interval = setInterval(check, CHECK_INTERVAL_MS)
    const initial = setTimeout(check, INITIAL_DELAY_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(interval)
      clearTimeout(initial)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const reload = useCallback(() => window.location.reload(), [])

  return (
    <Snackbar open={available} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        variant="filled"
        action={
          <Button color="inherit" size="small" onClick={reload}>
            Refresh
          </Button>
        }
      >
        A new version is available. Refresh to update.
      </Alert>
    </Snackbar>
  )
}

export default NewVersionNudge
